import {
  Group,
  NumberInput,
  Radio,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { ModalFooter } from 'components/ModalFooter';
import dayjs from 'dayjs';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { isNullish } from 'remeda';
import {
  getCurrencySymbolFromCurrencyCode,
  useLocalizedCurrencyFormatter,
} from 'shared/utils/currency';
import type { CurrencyCode } from 'types/currency';
import type { StoreState } from 'types/storeTypes';

type EncodePaymentFormValues = {
  amount: string;
  received_at: Date;
  notes: string;
  modality: string;
};

type EncodePaymentFormProps = {
  onSubmit: (values: EncodePaymentFormValues) => void;
  onClose: () => void;
  currency: CurrencyCode;
  totalAmount: number;
  amountWithoutFees?: number;
};

type RadioGroupValue = 'percentage' | 'fullAmountWithFees' | 'amountWithoutFees' | 'customAmount';

export default function EncodePaymentForm({
  onSubmit,
  onClose,
  currency,
  totalAmount,
  amountWithoutFees,
}: EncodePaymentFormProps) {
  const { t } = useTranslation();
  const [radioGroupValue, setRadioGroupValue] = useState<RadioGroupValue>('fullAmountWithFees');
  const [percentageValue, setPercentageValue] = useState('10');

  const constants = useSelector((state: StoreState) => state.app.constants);
  const formatter = useLocalizedCurrencyFormatter(false, currency);

  const form = useForm<EncodePaymentFormValues>({
    initialValues: {
      amount: String(totalAmount),
      received_at: dayjs().toDate(),
      notes: '',
      modality: '',
    },
    validate: {
      received_at: value => (value ? null : t(i18nKeys.ERROR.FIELD_REQUIRED)),
      modality: value => (value ? null : t(i18nKeys.ERROR.FIELD_REQUIRED)),
    },
  });

  useEffect(() => {
    if (radioGroupValue === 'percentage') {
      const newValue = (Number(percentageValue) / 100) * totalAmount;
      form.setFieldValue('amount', newValue.toFixed(2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radioGroupValue]);

  const handlePercentageChange = (value: string | number) => {
    setPercentageValue(String(value));
    changeRangeAmount(value);
  };

  const changeRangeAmount = (value: string | number) => {
    form.setFieldValue(
      'amount',
      (Math.round(((totalAmount * Number(value)) / 100) * 100) / 100).toFixed(2),
    );
  };

  const changeAmount = (value: string | number) => {
    form.setFieldValue('amount', Number(value).toFixed(2));
  };

  const amount = form.values.amount;

  const shouldDisplayAmountWithoutFees = () => {
    if (isNullish(amountWithoutFees)) return false;
    if (amountWithoutFees <= 0) return false;
    if (amountWithoutFees === totalAmount) return false;

    return true;
  };

  const modalityOptions = constants.payment_modalities.map(
    (modality: { value: string; description: string }) => ({
      value: modality.value,
      label: modality.description,
    }),
  );

  // Set default modality to first option if available and not already set
  useEffect(() => {
    if (modalityOptions.length > 0 && !form.values.modality) {
      form.setFieldValue('modality', modalityOptions[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalityOptions.length]);

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap={'lg'}>
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            {t(i18nKeys.MODALITY)}
          </Text>
          <SegmentedControl
            data={modalityOptions}
            key={form.key('modality')}
            {...form.getInputProps('modality')}
          />
        </Stack>
        <Radio.Group
          value={radioGroupValue}
          onChange={(value: string) => setRadioGroupValue(value as RadioGroupValue)}
        >
          <SimpleGrid cols={2} spacing="md">
            <Radio
              value="percentage"
              styles={{ body: { alignItems: 'center' } }}
              label={
                <Group>
                  <Text size="sm">{t(i18nKeys.SHARED.ENCODE_PAYMENT.PERCENTAGE)}</Text>
                  <NumberInput
                    value={percentageValue}
                    size="xs"
                    disabled={radioGroupValue !== 'percentage'}
                    onChange={handlePercentageChange}
                    w={80}
                    allowNegative={false}
                    suffix="%"
                    max={100}
                  />
                  {radioGroupValue === 'percentage' && (
                    <Text size="sm">
                      ({formatter.format((Number(percentageValue) / 100) * totalAmount)})
                    </Text>
                  )}
                </Group>
              }
            />
            <Radio
              value="customAmount"
              styles={{ body: { alignItems: 'center' } }}
              label={
                <Group>
                  <Text size="sm">{t(i18nKeys.SHARED.ENCODE_PAYMENT.CUSTOM_AMOUNT)}</Text>
                  <NumberInput
                    size="xs"
                    disabled={radioGroupValue !== 'customAmount'}
                    w={130}
                    value={amount}
                    allowNegative={false}
                    onChange={changeAmount}
                    suffix={getCurrencySymbolFromCurrencyCode(currency)}
                    max={totalAmount}
                  />
                </Group>
              }
            />
            <Radio
              value="fullAmountWithFees"
              styles={{ body: { alignItems: 'center' } }}
              onClick={() => changeAmount(totalAmount)}
              label={
                <Group>
                  <Text size="sm">{t(i18nKeys.SHARED.ENCODE_PAYMENT.FULL_AMOUNT)}</Text>
                  {radioGroupValue === 'fullAmountWithFees' && (
                    <Text size="sm">
                      ({totalAmount + getCurrencySymbolFromCurrencyCode(currency)})
                    </Text>
                  )}
                </Group>
              }
            />
            {shouldDisplayAmountWithoutFees() && (
              <Radio
                value="amountWithoutFees"
                styles={{ body: { alignItems: 'center' } }}
                onClick={() => changeAmount(amountWithoutFees ?? 0)}
                label={
                  <Group>
                    <Text size="sm">{t(i18nKeys.SHARED.ENCODE_PAYMENT.AMOUNT_WITHOUT_FEES)}</Text>
                    {radioGroupValue === 'amountWithoutFees' && (
                      <Text size="sm">
                        ({amountWithoutFees && formatter.format(amountWithoutFees)})
                      </Text>
                    )}
                  </Group>
                }
              />
            )}
          </SimpleGrid>
        </Radio.Group>
        <DatePickerInput
          label={t(i18nKeys.FORM.PAYMENT_DATE)}
          key={form.key('received_at')}
          {...form.getInputProps('received_at')}
        />
        <Textarea
          label={t(i18nKeys.NOTES)}
          key={form.key('notes')}
          {...form.getInputProps('notes')}
        />
        <ModalFooter onCancel={onClose} onSave={form.onSubmit(onSubmit)} withOwnPadding />
      </Stack>
    </form>
  );
}
